<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

// [VITE] [TODO] We are using vue-virtual-scroll for now, since that seemed the simplest way to migrate
// from the current one. But we should consider using tanstack virtual in the future
// https://tanstack.com/virtual/latest/docs/framework/vue/examples/variable
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import ChatListHeader from './ChatListHeader.vue';
import PhoneControls from './PhoneControls.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import IntersectionObserver from './IntersectionObserver.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useEventListener, useScrollLock } from '@vueuse/core';
import { format } from 'date-fns';

import { emitter } from 'shared/helpers/mitt';
import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import {
  conversationListPageURL,
  conversationListQuery,
  conversationUrl,
  frontendURL,
} from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { findNextConversation } from '../helper/conversationHelper';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { CMD_SELECT_NEXT_CONVERSATION } from '../helper/commandbar/events';

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const conversationListRef = ref(null);
const conversationDynamicScroller = ref(null);
const conversationListScrollableElement = computed(
  () => conversationDynamicScroller.value?.$el
);
const conversationListScrollLock = useScrollLock(
  conversationListScrollableElement
);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const customFilterQuery = ref({});
const dataAssistantExtraParams = ref(null);
const customFilterSortBy = ref(
  wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
);
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const appliedFilterSortBy = ref(
  wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);
const isEditingFolder = ref(false);
const isUpdatingFolder = ref(false);
const showQuickSearch = ref(false);
const quickSearchQuery = ref('');

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxesWithInternal');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentChat = useMapGetter('getSelectedChat');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

// computed
const intersectionObserverOptions = computed(() => {
  return {
    root: conversationListRef.value,
    rootMargin: '100px 0px 100px 0px',
  };
});

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderId = computed(() => {
  return activeFolder.value?.id;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const activeFolderTeamIds = computed(() => {
  return activeFolder.value?.team_ids || [];
});

const activeFolderParentId = computed(() => {
  return activeFolder.value?.parent_id || null;
});

const activeFolderColor = computed(() => {
  return activeFolder.value?.color || null;
});

const activeFolderEditable = computed(() => {
  return Boolean(activeFolder.value?.editable);
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasDataAssistantFilter = computed(() => {
  return !!dataAssistantExtraParams.value;
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return (
    hasAppliedFilters.value ||
    hasActiveFolders.value ||
    hasDataAssistantFilter.value
  );
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count: conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  if (hasAppliedFilters.value || hasDataAssistantFilter.value) {
    return 'appliedFilters';
  }
  if (hasActiveFolders.value) {
    return `folder_${props.foldersId}`;
  }
  return activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  ).count;
  return count;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    searchQuery: quickSearchQuery.value || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.internal_name || inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  // Apply folder filtering only if we're in a folder view AND no temporary filters are applied
  // Temporary filters (hasAppliedFilters) take precedence over folder filters
  if (activeFolder.value && !hasAppliedFilters.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload, {
        currentUserId: currentUser.value?.id,
      });
    });
  }

  return localConversationList;
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
  // [VITE] removing this since the library has changed
  // nextTick(() => {
  //   // Addressing a known issue in the virtual list library where dynamically added items
  //   // might not render correctly. This workaround involves a slight manual adjustment
  //   // to the scroll position, triggering the list to refresh its rendering.
  //   const virtualList = conversationListRef.value;
  //   const scrollToOffset = virtualList?.scrollToOffset;
  //   const currentOffset = virtualList?.getOffset() || 0;
  //   if (scrollToOffset) {
  //     scrollToOffset(currentOffset + 1);
  //   }
  // });
}

function fetchFilteredConversations() {
  let page = (currentFiltersPage.value || 0) + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: customFilterQuery.value,
      sortBy: customFilterSortBy.value,
      page,
      q: quickSearchQuery.value || undefined,
      extraParams: dataAssistantExtraParams.value || undefined,
    })
    .then(emitConversationLoaded);
}

function fetchSavedFilteredConversations() {
  let queryData = useSnakeCase(activeFolder.value.query);
  let sortBy =
    queryData.sort_by || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
  store.dispatch('setConversationFiltersSortBy', sortBy);
  const page = (currentFiltersPage.value || 0) + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: { payload: queryData.payload },
      sortBy,
      page,
      filterType: `folder_${props.foldersId}`,
      q: quickSearchQuery.value || undefined,
    })
    .then(emitConversationLoaded);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
  isEditingFolder.value = false;
}

// The list reads ad-hoc filters from the URL and conversation links keep them
// (conversationListQuery), so once the user replaces or clears such a filter the
// URL has to forget it too, or a reload would bring the old filter back.
let isDroppingUrlFilters = false;

function dropUrlFilters() {
  // The data assistant filter lives in the URL and in memory, drop both together.
  dataAssistantExtraParams.value = null;
  if (!conversationListQuery(route.query)) return;
  // Removing display_ids fires its watcher, which would reset the list again.
  isDroppingUrlFilters = !!route.query.display_ids;
  const { messageId } = route.query;
  router.replace({ query: messageId ? { messageId } : {} });
}

function onApplyFilter(payload, sortBy) {
  dropUrlFilters();
  resetBulkActions();
  payload = useSnakeCase(payload);
  customFilterQuery.value = filterQueryGenerator(payload);
  customFilterSortBy.value = sortBy;
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  closeAdvanceFiltersModal();
  fetchFilteredConversations();
}

async function onUpdateSavedFilter(
  payload,
  sortBy,
  folderName,
  teamIds,
  parentId,
  color
) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    team_ids: unref(teamIds),
    parent_id: unref(parentId),
    query: {
      ...filterQueryGenerator(transformedPayload),
      sort_by: sortBy,
    },
    color: unref(color),
  };
  if (isUpdatingFolder.value) return;
  isUpdatingFolder.value = true;
  try {
    await store.dispatch('customViews/update', payloadData);
    closeAdvanceFiltersModal();
  } catch (error) {
    useAlert(
      error?.message || t('FILTER.CUSTOM_VIEWS.EDIT.API_FOLDERS.ERROR_MESSAGE')
    );
  } finally {
    isUpdatingFolder.value = false;
  }
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
  appliedFilterSortBy.value = activeSortBy.value;
}

function initializeFolderToFilterModal() {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(activeFolder.value)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
  appliedFilterSortBy.value =
    unref(activeFolder.value)?.query?.sort_by ||
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function initializeAppliedFiltersToModal() {
  // Copies: the filter panel edits its rows in place, and the getter's objects
  // must keep the applied state when the panel is closed without applying.
  appliedFilter.value = appliedFilters.value.map(filter => ({ ...filter }));
  appliedFilterSortBy.value = customFilterSortBy.value;
}

function onToggleAdvanceFiltersModal(isEditMode = false) {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }
  isEditingFolder.value = isEditMode;

  if (hasActiveFolders.value && (isEditMode || !hasAppliedFilters.value)) {
    initializeFolderToFilterModal();
  } else if (hasAppliedFilters.value) {
    initializeAppliedFiltersToModal();
  } else {
    initializeExistingFilterToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations();
  } else {
    fetchConversations();
  }
}

function onResetFilters() {
  dropUrlFilters();
  customFilterQuery.value = {};
  resetAndFetchData();
}

function onCancelFilter() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations();
  }
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasDataAssistantFilter.value || hasAppliedFilters.value) {
    fetchFilteredConversations();
  } else if (hasActiveFolders.value) {
    fetchSavedFilteredConversations();
  }
}

// Add a method to handle scroll events
function handleScroll() {
  const scroller = conversationDynamicScroller.value;
  if (scroller && scroller.hasScrollbar) {
    const { scrollTop, scrollHeight, clientHeight } = scroller.$el;
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      loadMoreConversations();
    }
  }
}

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (!currentPage.value) {
      fetchConversations();
    } else {
      // The tab shows its cached list, so a request still in flight for the previous tab must not land.
      store.dispatch('invalidateConversationListRequests');
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    const {
      params: { accountId, inbox_id: inboxId, label, teamId },
      name,
      query,
    } = route;
    let conversationType = '';
    if (isOnMentionsView({ route: { name } })) {
      conversationType = 'mention';
    } else if (isOnUnattendedView({ route: { name } })) {
      conversationType = 'unattended';
    }
    router.push(
      conversationListPageURL({
        accountId,
        conversationType: conversationType,
        customViewId: props.foldersId,
        inboxId,
        label,
        teamId,
        query,
      })
    );
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}
async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(conversationId, status, snoozedUntil) {
  store
    .dispatch('toggleStatus', {
      conversationId,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    })
    .catch(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
    });
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return store.getters.getConversationById(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
  conversationListScrollLock.value = state;
}

// Where the open conversation last sat in the list, kept while it is still listed.
// Resolving drops it from a status-filtered list before the next one is picked.
// Forgotten when the list is swapped for another: switching tabs, or emptying the
// store to refetch (which deselects the conversation until it is loaded again).
const lastSelectedPosition = ref(null);
watch(
  [
    () => currentChat.value?.id,
    activeAssigneeTab,
    () =>
      conversationList.value.findIndex(
        conversation => conversation.id === currentChat.value?.id
      ),
  ],
  ([id, tab, index], [previousId, previousTab] = []) => {
    if (id !== previousId || tab !== previousTab) {
      lastSelectedPosition.value = null;
    }
    if (index !== -1) {
      lastSelectedPosition.value = { id, index };
    }
  },
  { immediate: true }
);

function selectNextConversation() {
  const next = findNextConversation(
    conversationList.value,
    currentChat.value.id,
    lastSelectedPosition.value
  );
  if (!next) return;

  const { conversation: nextConversation, wrapped } = next;
  if (wrapped) {
    // The DynamicScroller scrolls, not the wrapper div around it
    conversationDynamicScroller.value?.scrollToItem(0);
  }

  const {
    params: { accountId, inbox_id: inboxId, label, teamId, id: foldersId },
    name,
  } = route;
  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  const path = frontendURL(
    conversationUrl({
      accountId,
      id: nextConversation.id,
      activeInbox: inboxId,
      label: label,
      teamId: teamId,
      foldersId: foldersId,
      conversationType,
    }),
    conversationListQuery(route.query)
  );
  router.push(path);
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

function toggleQuickSearch() {
  showQuickSearch.value = !showQuickSearch.value;
  if (!showQuickSearch.value && quickSearchQuery.value !== '') {
    quickSearchQuery.value = '';
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    loadMoreConversations();
  }
}

function onQuickSearchInput(query) {
  if (!query || query.trim() === '') {
    quickSearchQuery.value = '';
  } else {
    quickSearchQuery.value = query;
  }
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  loadMoreConversations();
}

useEmitter('fetch_conversation_stats', () => {
  store.dispatch('conversationStats/get', conversationFilters.value);
});

useEmitter(CMD_SELECT_NEXT_CONVERSATION, selectNextConversation);

useEventListener(conversationDynamicScroller, 'scroll', handleScroll);

function applyDataAssistantFilter() {
  const {
    dam: dataAssistantMessageId,
    dari: dataAssistantReportId,
    ...colParams
  } = route.query;
  if (!dataAssistantMessageId && !dataAssistantReportId) return false;

  dataAssistantExtraParams.value = {
    ...(dataAssistantMessageId
      ? { dam: dataAssistantMessageId }
      : { dari: dataAssistantReportId }),
    ...colParams,
  };
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  activeStatus.value = 'all';
  store.dispatch('setChatStatusFilter', 'all');
  customFilterQuery.value = { payload: [] };
  fetchFilteredConversations();
  return true;
}

function applyPhoneStatusFilter() {
  const { phone_status: phoneStatus } = route.query;
  if (!phoneStatus) return false;

  const filterPayload = [
    {
      attribute_key: 'phone_call_type',
      attribute_model: 'standard',
      filter_operator: 'equal_to',
      values: [
        {
          id: phoneStatus,
          name: t(`FILTER.PHONE_CALL_TYPES.${phoneStatus.toUpperCase()}`),
        },
      ],
      query_operator: 'and',
    },
    {
      attribute_key: 'created_at',
      attribute_model: 'standard',
      filter_operator: 'is_greater_than_or_equal',
      values: [format(new Date(), 'yyyy-MM-dd')],
      query_operator: undefined,
    },
  ];

  store.dispatch('setConversationFilters', filterPayload);
  customFilterQuery.value = filterQueryGenerator(filterPayload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  activeStatus.value = 'all';
  store.dispatch('setChatStatusFilter', 'all');
  fetchFilteredConversations();
  return true;
}

// Opened from Reports → Bot when a thumbs up/down count is clicked. Builds a real,
// editable conversation filter scoped to the clicked rating, the row's inbox/label
// grouping and the report period.
function applyRatingFilter() {
  const {
    conversation_rating: rating,
    rating_group_by: groupBy,
    rating_group_value: groupValue,
    rating_since: since,
    rating_until: until,
  } = route.query;
  if (!rating) return false;

  const filterPayload = [
    {
      attribute_key: 'rating',
      attribute_model: 'standard',
      filter_operator: 'contains',
      values: [
        { id: rating, name: t(`FILTER.RATING_TYPES.${rating.toUpperCase()}`) },
      ],
      query_operator: 'and',
    },
  ];

  if (groupBy === 'inboxes' && groupValue) {
    const matchedInbox = inboxesList.value.find(
      item => `${item.id}` === `${groupValue}`
    );
    filterPayload.push({
      attribute_key: 'inbox_id',
      attribute_model: 'standard',
      filter_operator: 'equal_to',
      values: [
        { id: Number(groupValue), name: matchedInbox?.name || groupValue },
      ],
      query_operator: 'and',
    });
  } else if (groupBy === 'labels' && groupValue) {
    filterPayload.push({
      attribute_key: 'labels',
      attribute_model: 'standard',
      filter_operator: 'equal_to',
      values: [{ id: groupValue, name: groupValue }],
      query_operator: 'and',
    });
  }

  if (since) {
    filterPayload.push({
      attribute_key: 'created_at',
      attribute_model: 'standard',
      filter_operator: 'is_greater_than_or_equal',
      values: [since],
      query_operator: 'and',
    });
  }
  if (until) {
    filterPayload.push({
      attribute_key: 'created_at',
      attribute_model: 'standard',
      filter_operator: 'is_less_than_or_equal',
      values: [until],
      query_operator: 'and',
    });
  }

  // The final condition must not carry a trailing query_operator.
  filterPayload[filterPayload.length - 1].query_operator = undefined;

  store.dispatch('setConversationFilters', filterPayload);
  customFilterQuery.value = filterQueryGenerator(filterPayload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  activeStatus.value = 'all';
  store.dispatch('setChatStatusFilter', 'all');
  fetchFilteredConversations();
  return true;
}

// Opened from a link in a CC report. Shows exactly the listed conversations without
// creating a folder for them, so a report can link to an arbitrary set of tickets.
// conversations.display_id is a 4 byte integer; anything else is dropped rather than
// sent, so a mangled link degrades to the normal list instead of an empty one.
const MAX_DISPLAY_ID = 2147483647;

function parseDisplayIds(value) {
  return String(value ?? '')
    .split(',')
    .map(id => id.trim())
    .filter(id => /^[1-9]\d*$/.test(id) && Number(id) <= MAX_DISPLAY_ID);
}

function applyDisplayIdsFilter() {
  const displayIds = parseDisplayIds(route.query.display_ids);
  if (!displayIds.length) return false;

  const filterPayload = [
    {
      attribute_key: 'display_id',
      attribute_model: 'standard',
      filter_operator: 'equal_to',
      values: displayIds,
      query_operator: undefined,
    },
  ];

  store.dispatch('setConversationFilters', filterPayload);
  customFilterQuery.value = filterQueryGenerator(filterPayload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  activeAssigneeTab.value = wootConstants.ASSIGNEE_TYPE.ALL;
  activeStatus.value = 'all';
  store.dispatch('setChatStatusFilter', 'all');
  fetchFilteredConversations();
  return true;
}

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);

  const handled =
    applyDataAssistantFilter() ||
    applyPhoneStatusFilter() ||
    applyRatingFilter() ||
    applyDisplayIdsFilter();
  if (!handled) {
    resetAndFetchData();
  }

  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

onUnmounted(() => {
  conversationListScrollLock.value = false;
});

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('updateConversationStatus', toggleConversationStatus);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});
// The handlers above only run on mount, so opening a second report link while the
// list is already mounted would otherwise change the URL and nothing else.
watch(
  () => route.query.display_ids,
  (newVal, oldVal) => {
    if (newVal === oldVal) return;
    if (isDroppingUrlFilters) {
      isDroppingUrlFilters = false;
      return;
    }
    if (!applyDisplayIdsFilter() && parseDisplayIds(oldVal).length) {
      resetAndFetchData();
    }
  }
);

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 bg-n-solid-1 conversations-list-wrap"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[360px] 2xl:w-[420px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-folder-editable="activeFolderEditable"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :show-quick-search="showQuickSearch"
      :quick-search-query="quickSearchQuery"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal(true)"
      @advanced-filters-modal="onToggleAdvanceFiltersModal(false)"
      @reset-filters="onResetFilters"
      @cancel-filter="onCancelFilter"
      @basic-filter-change="onBasicFilterChange"
      @toggle-quick-search="toggleQuickSearch"
      @quick-search-input="onQuickSearchInput"
    />

    <Teleport v-if="showAddFoldersModal" to="#saveFilterTeleportTarget">
      <SaveCustomView
        :custom-views-query="customFilterQuery"
        :custom-views-sort-by="customFilterSortBy"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </Teleport>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex items-center justify-center p-4 overflow-auto"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <div
      ref="conversationListRef"
      class="flex-1 overflow-hidden conversations-list hover:overflow-y-auto"
      :class="{ 'overflow-hidden': isContextMenuOpen }"
    >
      <DynamicScroller
        ref="conversationDynamicScroller"
        :items="conversationList"
        :min-item-size="24"
        class="w-full h-full overflow-auto"
      >
        <template #default="{ item, index, active }">
          <!--
            If we encounter resizing issues, we can set the `watchData` prop to true
            this will deeply watch the entire object instead of just size dependencies
            But it can impact performance
          -->
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
            :size-dependencies="[
              item.messages,
              item.labels,
              item.uuid,
              item.inbox_id,
            ]"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              :is-on-expanded-layout="isOnExpandedLayout"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </DynamicScrollerItem>
        </template>
        <template #after>
          <div v-if="chatListLoading" class="text-center">
            <span class="mt-4 mb-4 spinner" />
          </div>
          <p
            v-else-if="showEndOfListMessage"
            class="p-4 text-center text-slate-400 dark:text-slate-300"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <IntersectionObserver
            v-else
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </template>
      </DynamicScroller>
    </div>
    <PhoneControls />
    <Teleport v-if="showAdvancedFilters" to="#conversationFilterTeleportTarget">
      <ConversationFilter
        v-model:filters="appliedFilter"
        v-model:sort-by="appliedFilterSortBy"
        :folder-id="activeFolderId"
        :folder-name="activeFolderName"
        :team-ids="activeFolderTeamIds"
        :parent-id="activeFolderParentId"
        :editable="activeFolderEditable"
        :color="activeFolderColor"
        :is-updating="isUpdatingFolder"
        :is-folder-view="
          hasActiveFolders && isEditingFolder && !hasAppliedFilters
        "
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </Teleport>
  </div>
</template>
